import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { API, axios } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { classi, messaggioErrore } from '../components/accesso/campi';
import {
  FILTRI, ORDINAMENTI, preparaElenco, conteggi, filtraEOrdina, titoloElenco, titoloImmobile, nomeFiltro as nomeDelFiltro, nomeOrdinamento,
} from '../utils/immobili';
import { testoInviata } from '../components/cliente/immobili/Decisione';
import { adessoRoma } from '../utils/percorso';
import { useTraduzioni } from '../i18n';
import Icona from '../components/cliente/Icone';
import { useScossone } from '../components/cliente/SezioniHome';
import { useConsulente } from '../components/cliente/useConsulente';
import CardImmobile from '../components/cliente/immobili/CardImmobile';
import Confronto from '../components/cliente/immobili/Confronto';
import '../components/cliente/immobili.css';

// La mappa (Leaflet) si carica solo quando serve
const MappaCase = React.lazy(() => import('../components/cliente/immobili/MappaCase'));

// Immobili del cliente (/immobili): bozza design/immobili-v3.html, vista "Elenco".
// Filtro e ordinamento stanno nell'indirizzo (/immobili?filtro=da_visitare&ordina=prezzo): tornando indietro dalla
// scheda si ritrovano, insieme al punto della pagina dove eravate (salvato in sessionStorage).

const OGNI_MINUTO = 60 * 1000;
const CHIAVE_SCROLL = 'mk_immobili_scroll';

const leggiScroll = () => {
  try { return JSON.parse(sessionStorage.getItem(CHIAVE_SCROLL) || 'null'); } catch { return null; }
};
const salvaScroll = (valore) => {
  try { sessionStorage.setItem(CHIAVE_SCROLL, JSON.stringify(valore)); } catch { /* senza memoria: pazienza */ }
};

const Aggiungi = ({ clienteId, onAggiunto }) => {
  const { t, errore: tradErrore } = useTraduzioni();
  const [link, setLink] = useState('');
  const [attesa, setAttesa] = useState(false);
  const inVolo = useRef(false); // Invio premuto di nuovo mentre si aspetta: non manda un secondo link
  const [avviso, setAvviso] = useState(null);
  const { scossa, scuoti, fine } = useScossone();
  const aggiungi = async (e) => {
    e.preventDefault();
    if (inVolo.current) return;
    if (!link.trim()) { setAvviso({ testo: t('home.aggiungi.manca_link') }); scuoti(); return; }
    inVolo.current = true;
    setAttesa(true);
    setAvviso(null);
    try {
      // Il testo va com'è: il server ne estrae il link (anche da "Guarda questo immobile… https://…")
      await axios.post(`${API}/clienti/${encodeURIComponent(clienteId)}/immobili`, { link: link.trim() });
      setLink('');
      setAvviso({ ok: true, testo: t('home.aggiungi.fatto') });
      onAggiunto();
    } catch (error) {
      setAvviso({ testo: tradErrore(messaggioErrore(error, t('home.aggiungi.errore'))) });
      scuoti();
    } finally {
      inVolo.current = false;
      setAttesa(false);
    }
  };
  return (
    <>
      <form className={classi('add', scossa && 'shake')} onAnimationEnd={fine} onSubmit={aggiungi}>
        <Icona nome="collegamento" />
        {/* type="text": con type="url" il browser rifiutava "www.idealista.it/…" e il testo condiviso dalle app */}
        <input type="text" inputMode="url" autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false}
          value={link} onChange={(e) => { setLink(e.target.value); setAvviso(null); }}
          placeholder={t('immobili.aggiungi_segnaposto')} aria-label={t('home.aggiungi.etichetta')} data-testid="link-annuncio" />
        <button type="submit" className={classi('btn p sm', attesa && 'busy')} aria-busy={attesa} disabled={attesa}>{t('home.aggiungi.bottone')}</button>
      </form>
      {avviso && <div className={classi('err', avviso.ok && 'ok')} role={avviso.ok ? 'status' : 'alert'}>{avviso.testo}</div>}
    </>
  );
};

const ImmobiliCliente = () => {
  const { user, userType } = useAuth();
  const { t, l } = useTraduzioni();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [nonAggiornato, setNonAggiornato] = useState(false); // rilettura fallita con l'elenco già a video
  const [rifiuto, setRifiuto] = useState(null); // il server ha rifiutato una risposta: il suo messaggio
  const [inviata, setInviata] = useState(null); // risposta mandata dalla card: { id, scelta, titolo, altezza }
  // l'ora di Roma, come le date delle visite (anche con il browser su un altro fuso)
  const [adesso, setAdesso] = useState(adessoRoma);
  const [parametri, setParametri] = useSearchParams();
  const { search, key: chiavePagina } = useLocation();
  const navigazione = useNavigationType();
  // Letti dall'indirizzo all'apertura; poi comanda lo stato e l'indirizzo lo segue (React Router aggiorna i
  // parametri in una transizione: due cambi rapidi leggerebbero l'indirizzo vecchio e perderebbero il primo)
  const filtroDa = (p) => (FILTRI.some((f) => f.chiave === p.get('filtro')) ? p.get('filtro') : 'attivi');
  const ordineDa = (p) => (ORDINAMENTI.some((o) => o.chiave === p.get('ordina')) ? p.get('ordina') : 'inserimento');
  const [filtro, setFiltro] = useState(() => filtroDa(parametri));
  const [ordinamento, setOrdinamento] = useState(() => ordineDa(parametri));
  // Un indirizzo nuovo che non viene da qui (link "Immobili" della barra, Indietro/Avanti del browser): lo si segue.
  // Prima con "Scartati" attivo il link della barra portava a /immobili ma restavano "Scartati" e l'ordine.
  // I nostri cambi di filtro e ordine sostituiscono l'indirizzo (REPLACE): lì comanda lo stato.
  useEffect(() => {
    if (navigazione === 'REPLACE') return;
    setFiltro(filtroDa(parametri));
    setOrdinamento(ordineDa(parametri));
  }, [chiavePagina]); // eslint-disable-line
  const [strumento, setStrumento] = useState(null); // 'confronta' | 'mappa'
  const [scelte, setScelte] = useState([]);
  const [tolta, setTolta] = useState(null); // la casa uscita dal confronto scegliendone una terza
  const consulente = useConsulente(userType === 'cliente' ? user?.id : null);
  const haDati = useRef(false);

  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('immobili.occhiello')}`;
    return () => { document.title = precedente; };
  }, [t]);

  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MINUTO);
    return () => clearInterval(t);
  }, []);

  // Vero se l'elenco è aggiornato. Se la rilettura non riesce e l'elenco c'è già, resta quello con un avviso
  const carica = useCallback(async () => {
    if (!user?.id) return false;
    const base = `${API}/clienti/${encodeURIComponent(user.id)}`;
    const [immobili, visite, proposte] = await Promise.allSettled([
      axios.get(`${base}/immobili`), axios.get(`${base}/visite`), axios.get(`${base}/proposte`),
    ]);
    // Visite e proposte decidono le etichette ("Visita fissata", "Proposta in corso", "Rogito…"): senza di loro
    // le etichette sarebbero inventate (Lela "Proposta in corso" invece del rogito). Servono tutte e tre, come
    // in home: se una non arriva si dice (elenco di prima con l'avviso, o "Riprovate").
    const buona = (r) => r.status === 'fulfilled' && Array.isArray(r.value?.data);
    if (![immobili, visite, proposte].every(buona)) {
      if (haDati.current) setNonAggiornato(true); else setErrore(true);
      return false;
    }
    haDati.current = true;
    setErrore(false);
    setNonAggiornato(false);
    setAdesso(adessoRoma());
    setDati({ immobili: immobili.value.data, visite: visite.value.data, proposte: proposte.value.data });
    return true;
  }, [user?.id]);

  useEffect(() => { if (userType === 'cliente') carica(); }, [userType, carica]);

  // Il server ha rifiutato una risposta dalla card ("Volete vederla?" con la casa già cambiata in un'altra finestra,
  // "Sposta" su una visita annullata): si rilegge l'elenco e si dice perché (come la scheda). Prima l'elenco restava
  // vecchio e il pannello si poteva rimandare all'infinito.
  const rifiutata = useCallback((messaggio) => {
    setRifiuto(`${String(messaggio || '').replace(/\.$/, '')}. ${t('immobili.elenco_aggiornato')}`);
    setInviata(null);
    carica();
  }, [carica, t]);
  // Risposta dalla card ("Sì, fissate la visita", "Non ci interessa"): si ricorda quale casa, così se con il filtro
  // attivo esce dall'elenco ("Sì" con "Da decidere", "Non ci interessa" con "Attivi") la conferma resta nell'avviso
  // della pagina. Prima la card spariva con la sua conferma dentro, senza dire niente. Lo spazio delle card resta alto
  // com'era finché c'è l'avviso: con la card in fondo la pagina si accorciava e il browser la faceva saltare in su
  // (a 390 px di una card intera).
  const postoCase = useRef(null);
  const risposta = useCallback((scelta, immobile) => {
    setRifiuto(null);
    setInviata(scelta && immobile
      ? { id: immobile.id, scelta, titolo: titoloImmobile(immobile, l), altezza: postoCase.current?.offsetHeight || 0 } : null);
    return carica();
  }, [carica, l]);

  const elenco = useMemo(() => (dati ? preparaElenco(dati.immobili, dati.visite, dati.proposte, adesso, l) : []), [dati, adesso, l]);
  const quanti = useMemo(() => conteggi(elenco), [elenco]);
  const mostrati = useMemo(() => filtraEOrdina(elenco, filtro, ordinamento), [elenco, filtro, ordinamento]);

  // Posizione nella pagina: si salva mentre si scorre; tornando indietro (POP) dalla scheda si ripristina
  useEffect(() => {
    if (!dati) return undefined;
    let fotogramma = null;
    const scorri = () => {
      if (fotogramma) return;
      fotogramma = requestAnimationFrame(() => { fotogramma = null; salvaScroll({ y: window.scrollY, search }); });
    };
    window.addEventListener('scroll', scorri, { passive: true });
    return () => { window.removeEventListener('scroll', scorri); if (fotogramma) cancelAnimationFrame(fotogramma); };
  }, [dati, search]);
  const ripristinato = useRef(false);
  useLayoutEffect(() => {
    if (!dati || ripristinato.current) return;
    ripristinato.current = true;
    const salvato = leggiScroll();
    if (navigazione === 'POP' && salvato && salvato.search === search && salvato.y > 0) window.scrollTo(0, salvato.y);
  }, [dati, navigazione, search]);

  if (userType === 'admin') return <Navigate to="/admin" replace />;
  if (userType === 'collaboratore') return <Navigate to="/collaboratore" replace />;

  if (!dati) {
    return (
      <main className="mk-home pagina-immobili wrap">
        <div className="attesa" role="status">
          {errore ? (
            <div className="err">{t('immobili.errore_caricamento')} <a href="#riprova" onClick={(e) => { e.preventDefault(); carica(); }}><u>{t('comune.riprova')}</u></a></div>
          ) : <span>{t('comune.caricamento')}</span>}
        </div>
      </main>
    );
  }

  // Filtro e ordinamento nell'indirizzo (tutti e due, dallo stato), senza aggiungere passi alla cronologia
  const cambia = ({ filtro: f = filtro, ordina: o = ordinamento }) => {
    if (f !== filtro) setInviata(null);
    setFiltro(f);
    setOrdinamento(o);
    const nuovi = new URLSearchParams(parametri);
    if (f === 'attivi') nuovi.delete('filtro'); else nuovi.set('filtro', f);
    if (o === 'inserimento') nuovi.delete('ordina'); else nuovi.set('ordina', o);
    setParametri(nuovi, { replace: true });
  };

  const titolo = titoloElenco(elenco, l);
  const titoloDi = (id) => titoloImmobile(elenco.find((e) => e.immobile.id === id)?.immobile || {}, l);
  // Una terza casa prende il posto della prima scelta: lo si dice
  const scegli = (id) => {
    if (scelte.includes(id)) { setScelte(scelte.filter((x) => x !== id)); setTolta(null); return; }
    setTolta(scelte.length === 2 ? titoloDi(scelte[0]) : null);
    setScelte([...scelte.slice(-1), id]);
  };
  const coppia = scelte.length === 2 ? scelte.map((id) => elenco.find((e) => e.immobile.id === id)).filter(Boolean) : [];
  const alternaStrumento = (nome) => { setStrumento((s) => (s === nome ? null : nome)); setScelte([]); setTolta(null); };
  const nomeFiltro = nomeDelFiltro(filtro, l);
  const nascoste = coppia.filter((e) => !mostrati.includes(e)).length;
  // La casa della risposta è uscita dall'elenco mostrato (con l'elenco riletto): la conferma e dove la trovate adesso
  const uscita = inviata && !mostrati.some((e) => e.immobile.id === inviata.id) ? inviata : null;
  const voceUscita = uscita && elenco.find((e) => e.immobile.id === uscita.id);
  const doveOra = voceUscita && (uscita.scelta === 'si' ? ['da_visitare', 'attivi'] : ['scartati'])
    .map((chiave) => FILTRI.find((f) => f.chiave === chiave)).find((f) => f.prende(voceUscita));
  const testoUscita = uscita && `${testoInviata(uscita.scelta, consulente, l)}${doveOra
    ? ` ${t('immobili.trovate_in', { titolo: uscita.titolo, filtro: nomeDelFiltro(doveOra.chiave, l) })}` : ''}`;
  const noteConfronto = [
    tolta && t('immobili.tolta_confronto', { titolo: tolta }),
    nascoste > 0 && t(nascoste === 1 ? 'immobili.una_nascosta' : 'immobili.due_nascoste', { filtro: nomeFiltro }),
  ];

  return (
    <main className="mk-home pagina-immobili wrap" data-testid="immobili-cliente">
      <div className="testa r">
        <div>
          <div className="k">{t('immobili.occhiello')}</div>
          <h1>{titolo.case}{titolo.decidere && <>, <em>{titolo.decidere}</em></>}</h1>
          <p>{t('immobili.sottotitolo')}</p>
        </div>
        {/* la casa aggiunta è tra le attive: con un filtro attivo ("Scartati") diceva "Aggiunto" ma la card non si vedeva */}
        <Aggiungi clienteId={user.id} onAggiunto={() => { if (filtro !== 'attivi') cambia({ filtro: 'attivi' }); return carica(); }} />
      </div>

      {elenco.length === 0 ? (
        <div className="vuoto-elenco r" data-testid="elenco-vuoto">
          <b>{t('immobili.vuoto_titolo')}</b>
          {t('immobili.vuoto_testo')}
        </div>
      ) : (
        <>
          <div className="tools r" role="toolbar" aria-label={t('immobili.strumenti')}>
            {FILTRI.map((f) => (
              <button type="button" key={f.chiave} className={classi('chip', filtro === f.chiave && 'on')} aria-pressed={filtro === f.chiave}
                onClick={() => cambia({ filtro: f.chiave })} data-testid={`filtro-${f.chiave}`}>
                {nomeDelFiltro(f.chiave, l)} <b>{quanti[f.chiave]}</b>
              </button>
            ))}
            <span className="sp" />
            <button type="button" className={classi('chip tool', strumento === 'confronta' && 'on')} aria-pressed={strumento === 'confronta'}
              onClick={() => alternaStrumento('confronta')}><Icona nome="confronta" />{t('immobili.confronta')}</button>
            <button type="button" className={classi('chip tool', strumento === 'mappa' && 'on')} aria-pressed={strumento === 'mappa'}
              onClick={() => alternaStrumento('mappa')}><Icona nome="luogo" />{t('immobili.sulla_mappa')}</button>
            <label className="sort">{t('immobili.ordina')}
              <select value={ordinamento} onChange={(e) => cambia({ ordina: e.target.value })} aria-label={t('immobili.ordina_per')}>
                {ORDINAMENTI.map((o) => <option key={o.chiave} value={o.chiave}>{nomeOrdinamento(o.chiave, l)}</option>)}
              </select>
            </label>
          </div>

          {strumento === 'confronta' && (coppia.length === 2
            ? <Confronto coppia={coppia} note={noteConfronto} onChiudi={() => { setScelte([]); setTolta(null); }} />
            : (
              <p className="avviso-strumento" role="status">
                {t(scelte.length === 1 ? 'immobili.scegliete_altra' : 'immobili.scegliete_due')}
                <button type="button" onClick={() => alternaStrumento('confronta')}>{t('comune.annulla')}</button>
              </p>
            ))}
          {strumento === 'mappa' && (
            <React.Suspense fallback={<div className="mappa-case" />}>
              <MappaCase elenco={mostrati} nomeFiltro={filtro === 'attivi' ? null : nomeFiltro} />
            </React.Suspense>
          )}

          <div ref={postoCase} style={uscita?.altezza ? { minHeight: uscita.altezza } : undefined} data-testid="posto-case">
            {mostrati.length === 0 ? (
              <div className="vuoto-elenco"><b>{t('immobili.niente_qui')}</b>{t('immobili.nessuna_nel_filtro', { filtro: nomeFiltro })}</div>
            ) : (
              <div className="griglia-case">
                {mostrati.map((voce) => (
                  <CardImmobile key={voce.immobile.id} voce={voce} consulente={consulente} adesso={adesso} onRisposta={risposta}
                    onRifiuto={rifiutata} visite={dati.visite}
                    selezione={strumento === 'confronta' ? { scelta: scelte.includes(voce.immobile.id), alterna: () => scegli(voce.immobile.id) } : null} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* rilettura non riuscita (dopo "Aggiungi" o una risposta): l'elenco di prima resta, con un avviso in basso */}
      {nonAggiornato ? (
        <div className="err avviso-pagina fisso" role="alert" data-testid="elenco-non-aggiornato">
          <span>{t('immobili.non_aggiornato')}</span>
          <button type="button" onClick={() => carica()}>{t('comune.riprova')}</button>
        </div>
      ) : rifiuto ? (
        <div className="err avviso-pagina fisso info" role="alert" data-testid="avviso-elenco">
          <span>{rifiuto}</span>
          <button type="button" onClick={() => setRifiuto(null)} aria-label={t('comune.chiudi_avviso')}>{t('comune.chiudi')}</button>
        </div>
      ) : uscita && (
        <div className="err ok avviso-pagina fisso" role="status" data-testid="conferma-elenco">
          <span>{testoUscita}</span>
          <button type="button" onClick={() => setInviata(null)} aria-label={t('comune.chiudi_avviso')}>{t('comune.chiudi')}</button>
        </div>
      )}
    </main>
  );
};

export default ImmobiliCliente;
